import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Awards } from '../model/award';
import { CartItem, CartSummary, CartSyncResult, toCartItem } from '../model/cart';

/**
 * Carrinho da Loja.
 *
 * Fica fora do array de produtos de proposito: `getAwards()` reconstroi a lista
 * (e zeraria as quantidades) sempre que o catalogo recarrega, e a barra de resumo,
 * os cards e o painel lateral precisam ler a mesma fonte.
 *
 * O espelho e em sessionStorage, nao localStorage: o token ja vive em
 * sessionStorage e `logout()` faz `sessionStorage.clear()`, entao o carrinho
 * morre junto com a sessao - importante em maquina compartilhada.
 */
@Injectable({
  providedIn: 'root',
})
export class CartService {
  private readonly STORAGE_KEY = 'clic.store.cart.v1';
  private readonly CHECKOUT_KEY = 'clic.store.checkout.v1';

  private source = new BehaviorSubject<CartItem[]>(this.readStorage());

  readonly items$: Observable<CartItem[]> = this.source.asObservable();
  readonly summary$: Observable<CartSummary> = this.items$.pipe(map((items) => this.toSummary(items)));
  readonly totalItems$: Observable<number> = this.items$.pipe(map((items) => this.sumQuantity(items)));
  readonly totalPoints$: Observable<number> = this.items$.pipe(map((items) => this.sumPoints(items)));

  get items(): CartItem[] {
    return this.source.getValue();
  }

  get totalItems(): number {
    return this.sumQuantity(this.items);
  }

  get totalPoints(): number {
    return this.sumPoints(this.items);
  }

  get isEmpty(): boolean {
    return this.items.length === 0;
  }

  getQuantity(awardId: string): number {
    const found = this.items.filter((item) => item.awardId === awardId)[0];
    return found ? found.quantity : 0;
  }

  /** Mapa awardId -> quantidade, para o template consultar em O(1). */
  quantityMap(): { [awardId: string]: number } {
    const result: { [awardId: string]: number } = {};
    this.items.forEach((item) => (result[item.awardId] = item.quantity));
    return result;
  }

  add(award: Awards, quantity: number = 1): void {
    const current = this.getQuantity(award.id);
    this.setQuantityFor(award, current + quantity);
  }

  increment(award: Awards): void {
    this.setQuantityFor(award, this.getQuantity(award.id) + 1);
  }

  decrement(awardId: string): void {
    this.setQuantity(awardId, this.getQuantity(awardId) - 1);
  }

  /** Ajusta a quantidade de um item que ja esta no carrinho. */
  setQuantity(awardId: string, quantity: number): void {
    const items = this.items.map((item) =>
      item.awardId === awardId ? { ...item, quantity: this.clamp(quantity, item.maxQuantity) } : item
    );
    this.commit(items.filter((item) => item.quantity > 0));
  }

  remove(awardId: string): void {
    this.commit(this.items.filter((item) => item.awardId !== awardId));
  }

  clear(): void {
    this.commit([]);
  }

  /**
   * Relê o espelho ao entrar na Loja.
   *
   * O construtor deste singleton roda uma vez so. `logout()` limpa o
   * sessionStorage, mas a saida por RE e navegacao SPA (sem reload), entao o
   * BehaviorSubject sobreviveria e o carrinho do usuario anterior vazaria para
   * o proximo na mesma aba. Reler aqui fecha isso.
   */
  reloadFromStorage(): void {
    const stored = this.readStorage();
    if (this.differsFrom(stored)) {
      this.source.next(stored);
    }
  }

  /**
   * Reconcilia o carrinho com um catalogo recem-carregado: tira o que sumiu ou
   * ficou inativo e corta o que passou do estoque atual.
   */
  syncWithCatalog(products: Awards[]): CartSyncResult {
    const byId: { [id: string]: Awards } = {};
    (products || []).forEach((product) => (byId[product.id] = product));

    const removed: string[] = [];
    const clamped: string[] = [];
    const next: CartItem[] = [];

    this.items.forEach((item) => {
      const product = byId[item.awardId];
      if (!product || Number(product.quantity) <= 0) {
        removed.push((product && product.name) || item.name || item.awardId);
        return;
      }
      const max = Number(product.quantity) || 0;
      const quantity = Math.min(item.quantity, max);
      if (quantity < item.quantity) {
        clamped.push(product.name);
      }
      next.push({ ...toCartItem(product, quantity) });
    });

    // Commit condicional: sem isso cada emissao do catalogo troca a identidade
    // do array e re-renderiza a grade inteira sem nada ter mudado.
    if (removed.length || clamped.length || this.differsFrom(next)) {
      this.commit(next);
    }
    return { removed, clamped };
  }

  toBuyItems(): Array<{ awardId: string; quantity: number }> {
    return this.items.map((item) => ({ awardId: item.awardId, quantity: item.quantity }));
  }

  /**
   * Chave de idempotencia da tentativa de troca. A mesma chave e reenviada
   * enquanto a tentativa nao concluir: a API tem indice unico por
   * (employeeId, idempotency_key) e devolve o resgate ja criado em vez de
   * debitar de novo.
   *
   * ATENCAO: ainda NAO chamada por ninguem. O campo `idempotencyKey` so existe
   * na branch `fix-loja-resgate` da API; enviar para a versao publicada quebra
   * a mutation. Ate la a protecao contra duplo envio e so o flag `submitting`
   * da tela. Quando a API subir, chamar isto no `createBuyMovDto`.
   */
  beginCheckout(): string {
    const existing = this.readCheckoutKey();
    if (existing) {
      return existing;
    }
    const key = this.newKey();
    this.writeCheckoutKey(key);
    return key;
  }

  endCheckout(): void {
    this.writeCheckoutKey(null);
    this.clear();
  }

  private differsFrom(next: CartItem[]): boolean {
    const current = this.items;
    if (current.length !== next.length) {
      return true;
    }
    return next.some((item, index) => {
      const before = current[index];
      return (
        !before ||
        before.awardId !== item.awardId ||
        before.quantity !== item.quantity ||
        before.unitValue !== item.unitValue ||
        before.maxQuantity !== item.maxQuantity ||
        before.name !== item.name ||
        before.image !== item.image ||
        before.category !== item.category
      );
    });
  }

  private clamp(quantity: number, max: number): number {
    const value = Math.floor(Number(quantity) || 0);
    if (value <= 0) {
      return 0;
    }
    return max > 0 ? Math.min(value, max) : 0;
  }

  private setQuantityFor(award: Awards, quantity: number): void {
    const max = Number(award.quantity) || 0;
    const next = this.clamp(quantity, max);
    const exists = this.items.some((item) => item.awardId === award.id);

    let items: CartItem[];
    if (!exists) {
      items = next > 0 ? this.items.concat([toCartItem(award, next)]) : this.items.slice();
    } else {
      items = this.items.map((item) =>
        // Reaproveita preco/estoque do catalogo atual ao mexer na quantidade.
        item.awardId === award.id ? { ...toCartItem(award, next) } : item
      );
    }
    this.commit(items.filter((item) => item.quantity > 0));
  }

  private commit(items: CartItem[]): void {
    this.source.next(items);
    this.writeStorage(items);
    // Mexeu no carrinho, a tentativa anterior de troca nao vale mais.
    this.writeCheckoutKey(null);
  }

  private toSummary(items: CartItem[]): CartSummary {
    return { items, totalItems: this.sumQuantity(items), totalPoints: this.sumPoints(items) };
  }

  private sumQuantity(items: CartItem[]): number {
    return (items || []).reduce((total, item) => total + item.quantity, 0);
  }

  private sumPoints(items: CartItem[]): number {
    return (items || []).reduce((total, item) => total + item.quantity * item.unitValue, 0);
  }

  private newKey(): string {
    // crypto.randomUUID nao existe nos alvos do browserslist do projeto.
    return (
      Date.now().toString(36) +
      '-' +
      Math.random().toString(36).slice(2, 12) +
      Math.random().toString(36).slice(2, 12)
    );
  }

  private readStorage(): CartItem[] {
    try {
      const raw = sessionStorage.getItem(this.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(parsed)) {
        return [];
      }
      const seen: { [awardId: string]: boolean } = {};
      return parsed
        .filter((item) => item && item.awardId && !seen[item.awardId] && (seen[item.awardId] = true))
        .map((item) => ({
          awardId: String(item.awardId),
          name: String(item.name || ''),
          image: String(item.image || ''),
          // Coage: quantity: "2" vindo do storage viraria concatenacao na soma.
          unitValue: Number(item.unitValue) || 0,
          quantity: Math.floor(Number(item.quantity)) || 0,
          maxQuantity: Math.floor(Number(item.maxQuantity)) || 0,
          category: String(item.category || ''),
        }))
        .filter((item) => item.quantity > 0);
    } catch (e) {
      return [];
    }
  }

  private writeStorage(items: CartItem[]): void {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // Aba anonima ou cota estourada: o carrinho segue so em memoria.
    }
  }

  private readCheckoutKey(): string {
    try {
      return sessionStorage.getItem(this.CHECKOUT_KEY) || '';
    } catch (e) {
      return '';
    }
  }

  private writeCheckoutKey(key: string | null): void {
    try {
      if (key) {
        sessionStorage.setItem(this.CHECKOUT_KEY, key);
      } else {
        sessionStorage.removeItem(this.CHECKOUT_KEY);
      }
    } catch (e) {
      // ignora
    }
  }
}
